import { Injectable } from '@angular/core';
import { API_ENDPOINT, GLOBAL_DATA_KEY, SESSION_STORAGE_KEY } from '@core/configs';
import { ApiService, AvatarDealerService, SessionStorageService } from '@core/services/common';
import { GlobalDataService } from '../common/global-data.service';
import { OfficeTypeRequestModel, DaiInfoRequestModel, PeriodRequestModel, BatheScheduledRequestModel } from '@core/models/request';
import { lstScreen } from '@core/configs/title-setting.config';
import { Observable, from, lastValueFrom, timer } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class OfficeService {
  constructor(
    private readonly http: ApiService,
    private readonly globalDataService: GlobalDataService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly avatarDealerService: AvatarDealerService
  ) {
  }

  getOfficeType(officeCd:string) {
    const corpPefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const partitionKey = `corp_${corpPefix}#office_${officeCd}`;
    const body = new OfficeTypeRequestModel(partitionKey, officeCd);
    const url = `${API_ENDPOINT.office.getOfficeType}`;
    return this.http.post(url, body);
  }

  getLatestSyncDate(officeCd: string, corpPrefix: string){
    const partitionKey = `corp_${corpPrefix}#office_${officeCd}`;
    const body = new OfficeTypeRequestModel(partitionKey, officeCd);
    const url = `${API_ENDPOINT.office.getLatestSyncDate}`;
    return this.http.post(url, body);
  }

  /**
   * Get dai info with avatar processing - Returns Observable
   * @param officeCd Office code
   * @param officeType Office type
   * @param isWaitingAvatarProcess Deprecated. Avatar processing is now handled by AvatarDealerService in the background.
   */
  getDaiInfo(officeCd: string, officeType: number, _isWaitingAvatarProcess: boolean = false): Observable<any> {
    const corpPefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const body = new DaiInfoRequestModel(
      corpPefix,
      officeCd,
      "",
      officeType,
      // screenCd của màn hình checkSheet
      lstScreen[0].value
    );
    const url = `${API_ENDPOINT.dai.getDaiInfo}`;
    const deviceToken = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.deviceToken) || '';

    const attempt = (remaining: number): Observable<any> =>
      this.http.postSilent(url, body).pipe(
        catchError(err => {
          if (remaining > 0) {
            console.log(`[Office] getDaiInfo failed, retrying in 60s... (${remaining} left)`);
            return timer(60000).pipe(switchMap(() => attempt(remaining - 1)));
          }
          console.log('[Office] getDaiInfo failed, no more retries left. Fallback to existing dai list.');
          this.writeClientLog(`${deviceToken} | error: dai.getDaiInfo ${this.stringifyError(err)}`);
          const existingDaiInfo = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.daiInfo) || [];
          return from([{ status: 200, result: existingDaiInfo }]);
        })
      );

    // NOTE: getDaiInfo only populates dai info now. Avatar requests are driven
    // per-screen by each consumer's own lifecycle (see AvatarDealerService
    // callers), not bulk-prefetched here — so a deep-link reload of one screen
    // requests the avatars it actually shows instead of relying on a one-time
    // post-login bulk fetch.
    return attempt(1).pipe(
      switchMap((response: any) => {
        const res = this.normalizeDaiInfoResponse(response);
        return from([res]);
      })
    );
  }

  /**
   * Optimized method to fetch daiInfo only for new users
   * @param newDaiCds Array of new daicd that need to be fetched
   * @param officeCd Office code
   * @param officeType Office type
   * @returns Updated daiInfo list (existing + new users)
   */
  getDaiInfoForNewUsers(newDaiCds: string[], officeCd: string, officeType: number): Observable<any> {
    if (!newDaiCds || newDaiCds.length === 0) {
      return from([{ status: 200, result: [] }]);
    }

    const corpPefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const body = new DaiInfoRequestModel(
      corpPefix,
      officeCd,
      "",
      officeType,
      lstScreen[0].value
    );
    const targetDaiCds = Array.from(new Set(newDaiCds.filter((daicd: string) => !!daicd)));
    body.DaiCds = targetDaiCds;
    const url = `${API_ENDPOINT.dai.getDaiInfo}`;
    const deviceToken = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.deviceToken) || '';

    const attempt = (remaining: number): Observable<any> =>
      this.http.postSilent(url, body).pipe(
        catchError(err => {
          if (remaining > 0) {
            console.log(`[Office] getDaiInfoForNewUsers failed, retrying in 60s... (${remaining} left)`);
            return timer(60000).pipe(switchMap(() => attempt(remaining - 1)));
          }
          console.log('[Office] getDaiInfoForNewUsers failed, no more retries left. Fallback to existing dai list.');
          this.writeClientLog(`${deviceToken} | error: dai.getDaiInfo ${this.stringifyError(err)}`);
          const existingDaiInfo = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.daiInfo) || [];
          return from([{ status: 200, result: existingDaiInfo }]);
        })
      );

    return attempt(1).pipe(
      switchMap((response: any) => {
        const res = this.normalizeDaiInfoResponse(response);
        if (res?.status === 200 && res?.result?.length > 0) {
          // Get existing daiInfo from session
          const existingDaiInfo = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.daiInfo) || [];
          const existingDaiCds = existingDaiInfo.map((item: any) => item.daicd);

          // Filter only new users from API response
          const newUsers = res.result.filter((item: any) =>
            newDaiCds.includes(item.daicd) && !existingDaiCds.includes(item.daicd)
          );

          if (newUsers.length > 0) {
            const newUserDaiCds = newUsers.map((item: any) => item.daicd);
            const mergedDaiInfo = [...existingDaiInfo, ...newUsers];
            this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.daiInfo, mergedDaiInfo);
            this.avatarDealerService.requestAvatars(newUserDaiCds);
            return from([{ status: 200, result: mergedDaiInfo }]);
          }
        }
        // Return existing data if no new users found
        const existingDaiInfo = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.daiInfo) || [];
        return from([{ status: 200, result: existingDaiInfo }]);
      })
    );
  }

  private writeClientLog(message: string) {
    const url = `${API_ENDPOINT.clientLog.write}`;
    this.http.postSilent(url, { message }).subscribe({
      error: () => { }
    });
  }

  private stringifyError(err: any): string {
    if (err == null) return '';
    if (typeof err === 'string') return err;
    const status = err.status != null ? `status ${err.status}` : '';
    const detail = err.message ?? err.statusText ?? JSON.stringify(err);
    return status ? `${status} - ${detail}` : detail;
  }

  private mapDaiInfoCsvResponse(res: any): any {
    if (typeof res?.result !== 'string') return res;
    return {
      ...res,
      result: this.parseCsvToObjects(res.result)
    };
  }

  private normalizeDaiInfoResponse(res: any): any {
    const mappedRes = this.mapDaiInfoCsvResponse(res);
    if (!Array.isArray(mappedRes?.result)) return mappedRes;
    return {
      ...mappedRes,
      result: mappedRes.result.map((item: any) => this.normalizeDaiInfoItem(item))
    };
  }

  private normalizeDaiInfoItem(item: any): any {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return item;

    return Object.entries(item).reduce((normalized: any, [key, value]) => {
      normalized[this.lowerFirst(key)] = value;
      return normalized;
    }, {});
  }

  private parseCsvToObjects(csv: string): any[] {
    if (!csv?.trim()) return [];

    const rows = this.parseCsvRows(csv);
    if (rows.length <= 1) return [];

    const headers = rows[0].map(header => this.lowerFirst(header.trim()));
    return rows.slice(1)
      .filter(row => row.some(value => value !== ''))
      .map(row => {
        const item: any = {};
        headers.forEach((header, index) => {
          item[header] = this.normalizeCsvValue(header, row[index] ?? '');
        });
        return item;
      });
  }

  private parseCsvRows(csv: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let value = '';
    let inQuotes = false;

    for (let index = 0; index < csv.length; index++) {
      const char = csv[index];
      const nextChar = csv[index + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          value += '"';
          index++;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (char === ',' && !inQuotes) {
        row.push(value);
        value = '';
        continue;
      }

      if ((char === '\n' || char === '\r') && !inQuotes) {
        if (char === '\r' && nextChar === '\n') index++;
        row.push(value);
        rows.push(row);
        row = [];
        value = '';
        continue;
      }

      value += char;
    }

    if (value.length > 0 || row.length > 0) {
      row.push(value);
      rows.push(row);
    }

    return rows;
  }

  private normalizeCsvValue(header: string, value: string): any {
    if (value === '') return '';
    if (value === 'true') return true;
    if (value === 'false') return false;
    if (header === 'sex' && /^-?\d+$/.test(value)) return Number(value);
    return value;
  }

  private lowerFirst(value: string): string {
    return value ? `${value.charAt(0).toLowerCase()}${value.slice(1)}` : value;
  }

  getBlockAndUnitInfo() {
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const corpCd = this.globalDataService.get(GLOBAL_DATA_KEY.corpCd);
    const body = JSON.stringify(officeCd);
    const url = `${API_ENDPOINT.office.getBlockAndUnitByOffice}`;
    return this.http.post(url, body).pipe(
      map((res: any) => {
        if (res?.status === 200 && res?.result?.isSuccessStatusCode) {
          const resData = JSON.parse(res?.result?.json);

          if (resData) {
            // filter theo corpCd
            const filteredBlocks = (resData.BlockList || []).filter((b: any) => b?.corpcd === corpCd);
            const filteredUnits = (resData.UnitList || []).filter((u: any) => u?.corpcd === corpCd);

            const filteredByCorp = {
              BlockList: filteredBlocks,
              UnitList: filteredUnits
            };

            return {
              ...res,
              result: {
                ...res.result,
                json: JSON.stringify(filteredByCorp)
              }
            };
          }
        }

        return res;
      })
    );
  }

  getGroupInfo() {
    const officeCd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    const body = JSON.stringify(officeCd);
    const url = `${API_ENDPOINT.office.getGroupByOffice}`;
    return this.http.post(url, body);
  }

   getLastSyncMaster(officeCd:string) {
    const corpPefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    const partitionKey = `corp_${corpPefix}#office_${officeCd}`;
    const body = new OfficeTypeRequestModel(partitionKey, officeCd);
    const url = `${API_ENDPOINT.office.getLastSyncMasterDate}`;
    return this.http.post(url, body);
  }

  getDaiByPeriod(sDate:string, eDate:string, officeType:number) {
    let body = new PeriodRequestModel()
    body.CorpPrefix = this.globalDataService.get(GLOBAL_DATA_KEY.corpPrefix);
    body.Officecd = this.globalDataService.get(GLOBAL_DATA_KEY.officeCd);
    body.Sdate = sDate;
    body.Edate = eDate;
    body.OfficeType = officeType;
    body.ScreenCd = "01";

    const url = `${API_ENDPOINT.dai.getDaiByPeriod}`;
    return this.http.post(url, body);
  }

  getBathScheduled(daiCds: string[], sDate: string, eDate: string, screenCd: string = "01") {
    const body = new BatheScheduledRequestModel(daiCds, sDate, eDate, screenCd);
    const url = `${API_ENDPOINT.dai.getBathScheduled}`;
    return this.http.post(url, body);
  }

  /**
   * Fetch valid schedule periods for new users and merge with existing session data
   * @param newDaiCds Array of new daicd that need to fetch schedule periods
   */
  async updateValidSchedulePeriodForNewUsers(newDaiCds: string[]): Promise<void> {
    try {
      if (!newDaiCds || newDaiCds.length === 0) return;

      // Get current time in format "yyyy-MM-dd HH:mm"
      const now = new Date();
      const jpTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
      const year = jpTime.getFullYear();
      const month = String(jpTime.getMonth() + 1).padStart(2, '0');
      const day = String(jpTime.getDate()).padStart(2, '0');
      const hours = String(jpTime.getHours()).padStart(2, '0');
      const minutes = String(jpTime.getMinutes()).padStart(2, '0');
      const anchorDate = `${year}-${month}-${day} ${hours}:${minutes}`;

      const url = `${API_ENDPOINT.checkSheet.getValidSchedulePeriod}`;
      const body = { daiCds: newDaiCds, anchorDate };

      const res: any = await lastValueFrom(this.http.post(url, body));

      if (res?.status === 200 && res?.result) {
        // Get existing valid schedule periods from session
        const existingPeriods = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.validSchedulePeriod) || {};

        // Merge new periods with existing (update, not overwrite)
        const mergedPeriods = {
          ...existingPeriods,
          ...res.result
        };

        // Save merged periods back to session
        this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.validSchedulePeriod, mergedPeriods);
      }
    } catch (error) {
      console.error("Update valid schedule period for new users failed: ", error);
    }
  }
}
